import { Box, Stack, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import parseJsonSafe from '../../components/Schedule/parseJsonSafe'
import { History } from '../../store/redux/reducers/auditSlice'
import DateFormat from '../../store/types/enums/DateFormat'
import { formatStringDate } from '../../store/utils/dateUtils'
import HistoryContentRows from './HistoryContentRows'

interface VacationAcknowledgementExpandableHistoryRowProps {
  data: History
}

const VacationAcknowledgementExpandableHistoryRow: React.FC<VacationAcknowledgementExpandableHistoryRowProps> = ({
  data,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const oldData = useMemo(() => parseJsonSafe(data.oldValue), [data.oldValue])
  const newData = useMemo(() => parseJsonSafe(data.newValue), [data.newValue])
  const isAddition = Object.keys(oldData).length === 0 && Object.keys(newData).length > 0
  const isDeletion = Object.keys(oldData).length > 0 && Object.keys(newData).length === 0

  const actionLabel = isAddition
    ? t('page.modification.acknowledged')
    : isDeletion
    ? t('page.modification.unacknowledged')
    : t('page.modification.acknowledgementModified')

  const dataSource = isAddition ? newData : oldData

  const actionColor = isAddition
    ? theme.palette.success.main
    : isDeletion
    ? theme.palette.error.main
    : theme.palette.info.main

  const vacation = dataSource.vacation

  return (
    <Box sx={{ mb: 2, borderRadius: 1, bgcolor: 'background.elevation2', p: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Typography variant="body1">{data.name}</Typography>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: actionColor, textAlign: 'right' }}>
            {actionLabel}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {dayjs(data.createdAt).format(DateFormat.HourMinute)}
          </Typography>
        </Stack>
      </Stack>
      {vacation && (
        <>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
              {t('page.modification.employee')}
            </Typography>
            <Typography variant="body2">{vacation.employee}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
              {t('page.modification.vacationPeriod')}
            </Typography>
            <Typography variant="body2">
              {formatStringDate(vacation.displayStartDate)} - {formatStringDate(vacation.displayEndDate)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
              {t('page.modification.vacationDescription')}
            </Typography>
            <Typography variant="body2">
              {vacation.description ? vacation.description : t('page.modification.noDescription')}
            </Typography>
          </Stack>
        </>
      )}
      <HistoryContentRows oldData={oldData} newData={newData} isAddition={isAddition} isDeletion={isDeletion} t={t} />
    </Box>
  )
}

export default VacationAcknowledgementExpandableHistoryRow
